// Day-by-day usage chart (stacked bar), broken out by the top models for
// the period (with a combined "Other" bucket) so the chart stays readable
// even when a period has many distinct models.
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Caption1, makeStyles, tokens } from '@fluentui/react-components';
import type { DailyUsageMap, ModelUsageMap } from '../../types/tokenusage';
import type { TooltipValueType } from 'recharts';
import { formatCompact, formatCount } from './formatUsage';

const MAX_SERIES = 6;
const OTHER_KEY = 'Other';

// Fluent UI brand/data-viz palette tokens, used in series order.
const SERIES_COLORS = [
  tokens.colorPaletteBlueForeground2,
  tokens.colorPaletteTealForeground2,
  tokens.colorPaletteGrapeForeground2,
  tokens.colorPaletteMarigoldForeground2,
  tokens.colorPaletteBerryForeground2,
  tokens.colorPaletteForestForeground2,
  tokens.colorNeutralForeground3, // "Other"
];

const useStyles = makeStyles({
  container: { width: '100%', height: '320px' },
  caption: { color: tokens.colorNeutralForeground3, display: 'block', marginBottom: tokens.spacingVerticalXS },
});

export interface UsageChartProps {
  dailyByModel: DailyUsageMap;
  totalsByModel: ModelUsageMap;
}

interface ChartRow {
  day: string;
  [model: string]: string | number;
}

function topModels(totalsByModel: ModelUsageMap, limit: number): string[] {
  return Object.entries(totalsByModel)
    .sort((a, b) => b[1].totalTokens - a[1].totalTokens)
    .slice(0, limit)
    .map(([model]) => model);
}

function buildChartData(dailyByModel: DailyUsageMap, series: string[]): ChartRow[] {
  const seriesSet = new Set(series);
  const days = Object.keys(dailyByModel).sort();
  return days.map((day) => {
    const models = dailyByModel[day];
    const row: ChartRow = { day: day.slice(-2) }; // just the day-of-month for a compact axis
    let other = 0;
    for (const [model, stat] of Object.entries(models)) {
      if (seriesSet.has(model)) {
        row[model] = (Number(row[model]) || 0) + stat.totalTokens;
      } else {
        other += stat.totalTokens;
      }
    }
    if (other > 0) row[OTHER_KEY] = other;
    return row;
  });
}

export function UsageChart(props: UsageChartProps) {
  const { dailyByModel, totalsByModel } = props;
  const styles = useStyles();

  const series = useMemo(() => topModels(totalsByModel, MAX_SERIES), [totalsByModel]);
  const data = useMemo(() => buildChartData(dailyByModel, series), [dailyByModel, series]);
  const hasOther = data.some((row) => row[OTHER_KEY] != null);
  const allSeries = hasOther ? [...series, OTHER_KEY] : series;

  if (data.length === 0) {
    return <Caption1>No daily usage data available for this period.</Caption1>;
  }

  return (
    <div>
      <Caption1 className={styles.caption}>Daily tokens by model (top {series.length} models{hasOther ? ' + other' : ''})</Caption1>
      <div className={styles.container}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" fontSize={12} />
            <YAxis tickFormatter={(v: number) => formatCompact(v)} fontSize={12} width={56} />
            <Tooltip formatter={(value?: TooltipValueType) => formatCount(Number(value ?? 0))} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {allSeries.map((model, i) => (
              <Bar key={model} dataKey={model} stackId="usage" fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default UsageChart;
